(function () {
    'use strict';

    angular
        .module('app')
        .factory('Email', EmailFactory);

    EmailFactory.$inject = [
        '$q',
        'EmailService',
        'DialogService',
        'Entity',
        'EntitiesService',
        'DocumentTemplatesService',
        'AttachmentList'];

    function EmailFactory(
        $q,
        EmailService,
        DialogService,
        Entity,
        EntitiesService,
        DocumentTemplatesService,
        AttachmentList) {

        /**
         * Get an existing email.
         *
         * @param {number} emailId
         *
         * @returns {Promise} A promise that resolves a {Email} object for the specified emailId.
         */
        function get(emailId) {
            return EmailService.getEmail(emailId)
                .then(function (data) {
                    return new Email(data);
                });
        }

        /**
         * Get a new email.
         *
         * @param {Array|Number} An array of entity ids, or a single entity id, to be used for the 'from addresses'
         *
         * @returns {Promise} A promise that resolves a new {Email} object.
         */
        function getNew(fromEntityIds) {
            var email = new Email();

            return validateFromEntityIds()
                .then(addEntityIdsToOwnerIds)
                .then(setDefaultEmailTemplate)
                .then(function () {
                    // It's technically not 'saved' data, however it is needed for the dirty check
                    email.savedData = angular.copy(email.data);
                    return email;
                });

            
            /**
             * Validate that the 'From' entity ids can all be obtained from the server and have a valid email address.
             *
             * @returns {Promise} Resolves if validation is OK, otherwise, rejects with a {string} error message.
             */
            function validateFromEntityIds() {
                if (!_.isNil(fromEntityIds)) {
                    if (!angular.isArray(fromEntityIds)) {
                        fromEntityIds = [fromEntityIds];
                    } else if (fromEntityIds.length > 1) {
                        email.data.MultipleFromMode = true;
                    }
                }

                var entityList = [];
                var getEntityPromises = [];
                
                if (!_.isNil(fromEntityIds)) {
                    angular.forEach(fromEntityIds, function (fromEntityId) {
                        getEntityPromises.push(Entity.get(fromEntityId)
                            .then(function (data) {
                                entityList.push(data);
                            }, function () {
                                entityList.push({ Id: fromEntityId, NotFound: true });
                            }));
                    });
                }

                return $q.all(getEntityPromises)
                    .then(function () {
                        var validationFailed = false;
                        var errorMessage = '';
                        
                        angular.forEach(entityList, function (entity) {
                            if (entity.NotFound) {
                                if (errorMessage !== '') {
                                    errorMessage += '\n\r';
                                }

                                errorMessage += 'Entity not found for entity id: ' + entity.Id + '.';
                                validationFailed = true;
                            } else if (angular.isUndefined(entity.EmailAddress) || entity.EmailAddress === null || entity.EmailAddress === '') {
                                if (errorMessage !== '') {
                                    errorMessage += '\n\r';
                                }

                                errorMessage += entity.EntityTemplate.Name + ' with id: ' + entity.Id + ' does not have a valid email address.';
                                validationFailed = true;
                            }
                        });

                        if (validationFailed) {
                            return $q.reject(errorMessage);
                        }
                    });
            }

            /**
             * Add all the entity id's to the email owner ids.
             *
             * @returns {Promise} Resolved once all entries are added
             */
            function addEntityIdsToOwnerIds() {
                if (!_.isNil(fromEntityIds)) {
                    email.data.OwnerIds = fromEntityIds;
                }

                return $q.resolve();
            }

            /**
             * Check for a default template, and if found, set properties on the email.
             *
             * @returns {Promise} resolved once default template has been set.
             */
            function setDefaultEmailTemplate() {
                return email.setDefaultEmailTemplate();
            }
        }

        /**
         * @constructor
         *
         * @property {Object} data - Email data, refer C# class 'Email'.
         */
        function Email(data) {
            var vm = this;
            vm.hasBeenUpdated = false;

            if (!_.isNil(data)) {
                vm.data = data;
            } else {
                vm.data = newEmail();
            }

            vm.Attachments = new AttachmentList(vm.data.Attachments, save, vm.data.Id);
            delete vm.data.Attachments;

            vm.savedData = angular.copy(vm.data);

            vm.delete = deleteEmail;
            vm.save = save;
            vm.send = send;
            vm.createResponse = createResponse;
            vm.getConversation = getConversation;
            vm.setEmailTemplate = setEmailTemplate;
            vm.getPreview = getPreview;
            vm.getValidationReport = getValidationReport;
            vm.changeFromAddress = changeFromAddress;
            vm.setDefaultEmailTemplate = setDefaultEmailTemplate;
            Object.defineProperty(vm, 'emailPersisted', { get: getEmailPersisted });
            Object.defineProperty(vm, 'dirty', { get: getDirty });
            Object.defineProperty(vm, 'validateEmail', { get: getValidateEmail });
            Object.defineProperty(vm, 'sendInProgress', { get: getSendInProgress });
            Object.defineProperty(vm, 'hasMergeField', { get: getHasMergeField });
            Object.defineProperty(vm, 'primaryEntityId', { get: getPrimaryEntityId });

            /**
             * Deletes the email.
             *
             * @returns {promise} Promise that will be resolved to a response object when the delete request succeeds of fails.
             */
            function deleteEmail() {
                return EmailService.remove(vm.data.Id);
            }

            /**
             * Saves email.
             * If the email is currently being saved, another save request will be chained onto the promise
             *
             * @returns {Promise} Promise that will be resolved to a response object when the save request succeeds or fails.
             */
            function save() {
                // A save operation is not allowed when send is in progress
                if (!_.isNil(send.sendPromise)) {
                    return $q.reject('Email send in progress.');
                }

                if (_.isNil(save.savePromise)) {
                    var currentEmail = angular.copy(vm.data);
                    delete currentEmail.Attachments;

                    var emailSavePromise = EmailService.save(currentEmail, true)
                        .then(saveEmailOk, saveEmailError);

                    save.savePromise = emailSavePromise;

                    return emailSavePromise;
                } else {
                    return save.savePromise.then(function () {
                        return save();
                    }, function () {
                        return save();
                    });
                }

                function saveEmailOk(response) {
                    vm.hasBeenUpdated = true;
                    vm.data.Id = response.Id;
                    vm.data.UpdatedBy = response.UpdatedBy;
                    vm.data.UpdatedDate = response.UpdatedDate;

                    currentEmail.Id = response.Id;
                    currentEmail.UpdatedBy = response.UpdatedBy;
                    currentEmail.UpdatedDate = response.UpdatedDate;

                    if (_.isNil(vm.data.CreatedBy) && _.isNil(currentEmail.CreatedBy)) {
                        vm.data.CreatedBy = response.UpdatedBy;
                        currentEmail.CreatedBy = response.UpdatedBy;
                    }

                    vm.savedData = angular.copy(currentEmail);
                    save.savePromise = null;

                    return response;
                }

                function saveEmailError(response) {
                    save.savePromise = null;

                    return $q.reject(response);
                }
            }

            /**
             * Sends the email.
             * If the email is currently being saved, the send process will be appended onto the end of the save. Otherwise a new save process will be started.
             *
             * @param {array} sendEmails - The emails to be sent from the draft and their recipients. Optional. If not supplied all possible emails will be sent from the draft.
             *
             * @returns {Promise} Promise that will be resolved to a response object when the send request succeeds or fails.
             */
            function send(sendEmails) {
                if (!_.isNil(save.savePromise)) {
                    return $q.all([save.savePromise]).then(function () {
                        return save().then(function () {
                            send.sendPromise = EmailService.sendEmail(vm.data.Id, sendEmails)
                                                            .then(sendOk, sendError);
                            return send.sendPromise;
                        });
                    });
                } else {
                    return save().then(function () {
                        send.sendPromise = EmailService.sendEmail(vm.data.Id, sendEmails)
                                                            .then(sendOk, sendError);
                        return send.sendPromise;
                    });
                }

                function sendOk(response) {
                    delete send.sendPromise;

                    return $q.when(response);
                }

                function sendError(response) {
                    delete send.sendPromise;

                    return $q.reject(response);
                }
            }

            /**
             * Generates a list of validation errors for the email
             *
             * @returns {array} List of validation errors for the email
             */
            function getValidateEmail() {
                var result = [];

                if (_.isNil(vm.data.Subject) || vm.data.Subject === '') {
                    result.push('Email subject is required');
                }

                if (!_.some(vm.data.To, function (address) { return EmailService.validateEmailAddress(address.Value).Valid || ((!_.isNil(address.EntityDetails) && !_.isNil(address.EntityDetails.Field) && address.EntityDetails.Field !== '')); }) &&
                    !_.some(vm.data.Cc, function (address) { return EmailService.validateEmailAddress(address.Value).Valid; }) &&
                    !_.some(vm.data.Bcc, function (address) { return EmailService.validateEmailAddress(address.Value).Valid; })) {
                    result.push('At least one recipient is required');
                }

                return result;
            }

            /**
             * Creates an email response using this email as the base.
             * Warning: If more than one entity is linked to the email, a dialog will be shown.
             *
             * @returns {Promise} Promise that will be resolved to a response object when the create response request succeeds or fails.
             *                  Cancellation of the 'select linked entity' dialog will result in a fail response with value of 'Cancelled'.
             */
            function createResponse(responseType) {
                return EmailService.getEmailRelatedEntities(vm.data.Id)
                    .then(getRelatedEntitiesOk, null);

                function getRelatedEntitiesOk(relatedEntities) {
                    if (!_.isNil(relatedEntities) && relatedEntities.length > 0) {
                        // If there is one linked entity, it should be used as the from address contact, otherwise if there's more than one the user will have to be prompted with a
                        // list of entities so the correct linked entity can be selected.
                        if (relatedEntities.length === 1) {
                            return EmailService.createResponse(vm.data.Id, relatedEntities[0], responseType);
                        } else {
                            var entitiesPromiseList = [];
                            var entityList = [];

                            angular.forEach(relatedEntities, function (relatedEntityId) {
                                entitiesPromiseList.push(Entity.get(relatedEntityId).then(function (data) { entityList.push(data); }));
                            });

                            return $q.all(entitiesPromiseList)
                                .then(function () {

                                    return DialogService.show({
                                        locals: {
                                            pageHeading: 'Select "From Address" contact',
                                            entityList: _.sortBy(entityList, function (n) { return n.PreviewNameText; })
                                        },
                                        controller: 'EntitySelectionListCtrl',
                                        controllerAs: 'vm',
                                        templateUrl: 'app/controls/entity/entity-selection-list.tmpl.html'
                                    })
                                    .then(function (fromEntityId) {
                                        return EmailService.createResponse(vm.data.Id, fromEntityId, responseType);
                                    }, function () {
                                        return $q.reject('Cancelled');
                                    });
                                }, null);
                        }
                    } else {
                        return $q.reject('Unable to find related entities for current email.');
                    }
                }
            }

            /**
             * Gets the conversation items for the email.
             *
             * @returns {Promise} A promise which will resolve with a list of emails linked to the conversation.
             */
            function getConversation() {
                return EmailService.getConversation(vm.data.Id)
                    .then(getConversationOk);

                function getConversationOk(data) {
                    var emailList = [];

                    angular.forEach(data, function (emailData) {
                        emailList.push(new Email(emailData));
                    });

                    return _.sortBy(emailList, function (n) {
                        return n.data.Date;
                    }).reverse();
                }
            }

            /**
             * Gets a 'preview' for the email, which is an email object with all of the send pre-processing conducted on the email.
             *
             * @param {number} entityId - Preview should use the supplied entity for resolving merge fields. If not supplied, the first 'From entity' will be used. Optional.
                                           If not supplied, the primary entity id will be used.
             * @returns {Promise} A promise which will resolve with an email object which can be used for showing a preview of the email with send pre-processing conducted.
             */
            function getPreview(entityId) {
                return save()
                    .then(function () {
                        if (_.isNil(entityId)) {
                            entityId = getPrimaryEntityId();
                        }

                        return EmailService.getPreview(vm.data.Id, entityId)
                            .then(getPreviewOk);

                        function getPreviewOk(data) {
                            return new Email(data);
                        }
                    });
            }

            /**
             * Gets a validation report for the email, which is used to show the pre-send dialog information for Multi Mode emails.
             *
             * @returns {Promise} A promise which will resolve with a validation report object.
             */
            function getValidationReport() {
                return save()
                    .then(function () {
                        return EmailService.getValidationReport(vm.data.Id);
                    });
            }

            /**
             * Set email properties according to the default template.
             *
             * @returns {Promise} Resolves once the default email template has been loaded and use to set email properties.
             */
            function setDefaultEmailTemplate() {
                if (!_.isNil(vm.primaryEntityId)) {
                    return EntitiesService.getDefaultEmailTemplate(vm.primaryEntityId)
                        .then(function (emailTemplate) {
                            if (!_.isNil(emailTemplate)) {
                                return setEmailTemplate(emailTemplate);
                            } else {
                                return $q.resolve();
                            }
                        });
                } else {
                    return $q.resolve();
                }
            }

            /**
             * Retrieves the document template files referenced by the email template and uses these to populate the corresponding field on the email.
             *
             * @returns {Promise} A promise that resolves once the email template has been loaded and used to set the email Preheader, Subject and Body.
             */
            function setEmailTemplate(emailTemplate) {
                var setEmailTemplatePromises = [];
                var preheader = null;
                var subject = null;
                var body = null;
                var sourceTemplateIds = [];

                if (!_.isNil(emailTemplate.Id) && emailTemplate.Id !== '') {
                    sourceTemplateIds.push(emailTemplate.Id);
                }

                if (!_.isNil(emailTemplate.FromAddressOverrideEntityId) && emailTemplate.FromAddressOverrideEntityId !== '') {
                    vm.data.FromAddressOverrideEntityId = emailTemplate.FromAddressOverrideEntityId;
                }

                if (!_.isNil(emailTemplate.To) && angular.isArray(emailTemplate.To) && emailTemplate.To.length > 0) {
                    vm.data.To = angular.copy(emailTemplate.To);
                }

                if (!_.isNil(emailTemplate.Cc) && angular.isArray(emailTemplate.Cc) && emailTemplate.Cc.length > 0) {
                    vm.data.Cc = angular.copy(emailTemplate.Cc);
                }

                if (!_.isNil(emailTemplate.Bcc) && angular.isArray(emailTemplate.Bcc) && emailTemplate.Bcc.length > 0) {
                    vm.data.Bcc = angular.copy(emailTemplate.Bcc);
                }

                if (!_.isNil(emailTemplate.ReplyTo) && angular.isArray(emailTemplate.ReplyTo) && emailTemplate.ReplyTo.length > 0) {
                    vm.data.ReplyTo = angular.copy(emailTemplate.ReplyTo);
                }

                if (!_.isNil(emailTemplate.Preheader) && emailTemplate.Preheader !== '') {
                    sourceTemplateIds.push(emailTemplate.Preheader);
                    setEmailTemplatePromises.push(DocumentTemplatesService.getDocumentFile(emailTemplate.Preheader).then(getPreheaderFileOk));
                }
                if (!_.isNil(emailTemplate.Subject) && emailTemplate.Subject !== '') {
                    sourceTemplateIds.push(emailTemplate.Subject);
                    setEmailTemplatePromises.push(DocumentTemplatesService.getDocumentFile(emailTemplate.Subject).then(getSubjectFileOk));
                }
                if (!_.isNil(emailTemplate.Body) && emailTemplate.Body !== '') {
                    sourceTemplateIds.push(emailTemplate.Body);
                    setEmailTemplatePromises.push(DocumentTemplatesService.getDocumentFile(emailTemplate.Body).then(getBodyFileOk));
                }

                function getPreheaderFileOk(response) {
                    preheader = String.fromCharCode.apply(null, new Uint8Array(response.data));
                }

                function getSubjectFileOk(response) {
                    subject = String.fromCharCode.apply(null, new Uint8Array(response.data));
                }

                function getBodyFileOk(response) {
                    body = String.fromCharCode.apply(null, new Uint8Array(response.data));
                }

                return $q.all(setEmailTemplatePromises)
                    .then(function () {
                        // The values should only be set on the email data if all of the document templates were successfully retrieved.
                        if (!_.isNil(preheader)) {
                            vm.data.Preheader = preheader;
                        }
                        if (!_.isNil(subject)) {
                            vm.data.Subject = subject;
                        }
                        if (!_.isNil(body)) {
                            vm.data.Body = body;
                        }

                        vm.data.SourceTemplateIds = sourceTemplateIds;
                    });
            }

            /**
             * Shows a dialog which allows the from address to be changed.
             */
            function changeFromAddress() {
                return DialogService.show({
                    locals: { fromAddressOverrideEntityId: vm.data.FromAddressOverrideEntityId },
                    controller: 'EmailSelectFromDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/email/email-select-from-dialog.tmpl.html',
                    size: DialogService.sizes.large
                })
                .then(function (fromAddressOverrideEntityId) {
                    vm.data.FromAddressOverrideEntityId = fromAddressOverrideEntityId;
                });
            }

            function getSendInProgress() {
                return !_.isNil(send.sendPromise);
            }

            /*
             * Checks to see if the email contains a merge field in the subject, preheader or body.
             *
             * @returns {bool} True there is a merge field contained within the subject, preheader or body. Otherwise false.
             */
            function getHasMergeField() {
                var mergeFieldRegex = /\[\[ChilliDocs:.*\]\]/;

                return (mergeFieldRegex.test(vm.data.Subject) || mergeFieldRegex.test(vm.data.Preheader) || mergeFieldRegex.test(vm.data.Body));
            }

            /**
             * A draft email is associated with one or more entities, used for field merging.
             * The "primary" entity ID is just the first of those entities.
             *
             * @returns {number} Primary entity id for the email, may be null.
             */
            function getPrimaryEntityId() {
                if (!_.isNil(vm.data.PrimaryEntityId)) {
                    return vm.data.PrimaryEntityId;
                } else if (vm.data.OwnerIds.length > 0) {
                    return vm.data.OwnerIds[0];
                } else {
                    return null;
                }
            }

            function getEmailPersisted() {
                var result = false;

                if (!_.isNil(vm.data.Id)) {
                    result = true;
                }

                return result;
            }

            function getDirty() {
                var result = false;

                var currentEmail = angular.copy(vm.data);
                delete currentEmail.Attachments;

                if (!angular.equals(currentEmail, vm.savedData)) {
                    result = true;
                }

                return result;
            }

            function newEmail() {
                return {
                    Status: 'Draft',
                    To: [],
                    Cc: [],
                    Bcc: [],
                    From: [],
                    ReplyTo: [],
                    Subject: '',
                    Preheader: '',
                    Body: '',
                    Attachments: [],
                    MultipleFromMode: false,
                    FromAddress: { Type: 'CurrentEntity' },
                    Tags: [],
                    SourceTemplateIds: [],
                    OwnerIds: []
                };
            }
        }

        Email.prototype = {};
        Email.prototype.constructor = Email;

        Email.get = get;
        Email.getNew = getNew;

        return Email;
    }
})();